<template>
  <modal :open="internalOpen" :width="1400">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <div class="flex flex-col gap-y-5 my-5 pb-5">
        <Multiselect
          v-if="isAdmin"
          v-model="rawData.type"
          :error="errors?.type"
          :options="possibleTypes"
          :label="$t('mountingTimeslotRegistration.type.DEFAULT')"
          :can-deselect="false"
          :can-clear="false"
        />
        <Multiselect
          v-if="isAdmin"
          v-model="rawData.campaignRegistration"
          :error="errors?.campaignRegistration"
          :label="$t('mountingTimeslotRegistration.campaign')"
          :options="campaignRegistrations"
          :can-deselect="false"
          :can-clear="false"
          :searchable="true"
          :loading="loadingCampaignRegistrations"
        />
        <text-area
          v-model="rawData.adminComment"
          :label="$t('mountingTimeslotRegistration.adminComment')"
          class="w-full"
          :disabled="!isAdmin"
        />
        <text-area
          v-model="rawData.comment"
          :error="errors?.comment"
          :label="$t('mountingTimeslotRegistration.comment')"
          class="w-full"
        />
      </div>

      <form-card
        v-if="mountingTimeslotRegistration.status === 'completed' && mountingTimeslotRegistration.type !== 'unmounting' && isAdmin"
        :title="$t('pages.mountings.mounting.mountingRegistrations.modal.box.title')"
        :description="
          $t('pages.mountings.mounting.mountingRegistrations.modal.box.manualDescription')
        "
        variant="singleColumn"
      >
        <div class="flex flex-col gap-y-5">
          <div class="flex gap-x-5">
            <FormInput
              v-model="rawData.box.sealNumber"
              :error="errors?.sealNumber"
              type="text"
              class="w-1/3"
              :label="$t('box.seal')"
            />

            <Multiselect
              v-model="rawData.box.type"
              :error="errors?.type"
              :options="boxTypes"
              :label="$t('box.type')"
              :can-deselect="false"
              :can-clear="false"
              class="w-1/3"
            />
            <Multiselect
              v-model="rawData.box.variant"
              :error="errors?.variant"
              :options="boxVariants"
              :label="$t('box.variant.DEFAULT')"
              :can-deselect="false"
              :can-clear="false"
              class="w-1/3"
            />
            <div class="w-1/3">
              <DatePicker
                v-model="rawData.box.mountingDate"
                :error="errors?.mountingDate"
                mode="datetime"
                :label="$t('box.mountingDate')"
                class="w-full"
              />
            </div>
          </div>
          <Multiselect
            v-model="rawData.box.status"
            :error="errors?.status"
            :options="enumOptionsProvider('/enums/BoxStatus', (item) => ({
                label: $t(`box.status.${item}`),
                value: item,
              }))"
            :label="$t('box.statusTitle')"
            :can-deselect="false"
            class="w-full"
            :can-clear="false"
          />

          <Multiselect
            v-model="rawData.box.firstCampaign"
            :error="errors?.firstCampaign"
            :label="$t('box.campaign')"
            :options="campaignList"
            :can-deselect="false"
            :can-clear="false"
            :searchable="true"
            :minChars="2"
            :filterResults="false"
            :delay="500"
            :resolveOnLoad="true"
            :createOption="false"
            :clearOnSelect="true"
            :noOptionsText="$t('inputSearchString')"
          />
          <div class="flex gap-x-5">
            <FormInput
              v-model="rawData.box.alreadyPayed"
              :error="errors?.alreadyPayed"
              type="number"
              :label="$t('box.alreadyPayed')"
              class="w-1/3"
            />
            <FormInput
              v-model="rawData.box.totalPrice"
              type="number"
              :label="$t('box.totalPrice')"
              class="w-1/3"
              disabled
            />
            <FormInput
              v-model="rawData.box.depot"
              type="number"
              :label="$t('box.depot')"
              class="w-1/3"
              disabled
            />
          </div>
        </div>
      </form-card>
      <div class="w-full text-center">
        <custom-button
          variant="primary"
          class="mt-15"
          :busy="loading"
          @click="save"
        >
          {{
            $t('save')
          }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import FormInput from '@utils/inputs/Input.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import DatePicker from '@components/utils/inputs/DatePicker.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import {
  collOptionsProvider,
  enumOptionsProvider,
  type ProviderParams,
} from '@utils/table/hooks/provider';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import FormCard from '@utils/FormCard.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import type { Option } from '@utils/filter/filterTypes/Dropdown.vue';
import type {
  Box,
  Campaign,
  CampaignRegistration,
  MountingTimeslotRegistration,
} from '@/entities/Entities';
import type { ByReference } from '@/utils/types';
import { useStore } from '@/store';

interface Props {
  open: boolean;
  mountingTimeslotRegistration?: MountingTimeslotRegistration;
}

const emit = defineEmits(['update:open', 'update:mountingTimeslotRegistration', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalMountingTimeslotRegistration = useVModel(props, 'mountingTimeslotRegistration', emit);

const isNew = computed(
  () => !props.mountingTimeslotRegistration?.campaignRegistration?.driver?.box,
);
const tKey = 'pages.mountings.mounting.mountingRegistrations.modal.manual';

const store = useStore();
const isAdmin = store.getters['auth/IS_ADMIN'];

const { t } = useI18n();

const notify = useNotify();

const possibleTypes = enumOptionsProvider('/enums/MountingTimeslotRegistrationType', (item) => ({
  value: item,
  label: t(`mountingTimeslotRegistration.type.${item}`),
}));

const filter = computed(() => ({ driver: props.mountingTimeslotRegistration?.driver?.id }));

const campaignRegistrations = ref<Option[]>([]);
const loadingCampaignRegistrations = ref(false);

const campaignList = (query: string, select: any) => {
  const input = query || select.modelValue?.split('/')?.slice(-1)?.[0];
  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'group.name': input } : { id: input },
    sort: {
      sortKey: 'group.name',
      sortDirection: 'ASC',
    },
  };
  return collOptionsProvider<Campaign>('/campaigns', filterQuery, (value) => ({
    label: value.name,
    value: value['@id'],
  }))();
};

const loadCampaignRegistrations = async (id: string) => {
  loadingCampaignRegistrations.value = true;
  campaignRegistrations.value = await collOptionsProvider<CampaignRegistration>(
    '/campaign_registrations',
    {
      filter: { driver: id },
      sort: {
        sortKey: 'group.name',
        sortDirection: 'ASC',
      },
    },
    (value) => ({
      label: value.campaign.name,
      value: value['@id'],
    }),
  )();
  loadingCampaignRegistrations.value = false;
};

watch(() => internalMountingTimeslotRegistration.value, (newVal) => {
  if (newVal?.driver) {
    loadCampaignRegistrations(newVal.driver.id);
  } else {
    campaignRegistrations.value = [];
  }
});

const initialBox: Partial<ByReference<Box, 'firstCampaign'>> = {
  mountingDate: undefined,
  type: undefined,
  variant: undefined,
  sealNumber: undefined,
  status: undefined,
  firstCampaign: undefined,
  alreadyPayed: 0,
};

const initialData = (mountingTimeslotRegistration?: MountingTimeslotRegistration) =>
  (mountingTimeslotRegistration
    ? {
      oldBox: mountingTimeslotRegistration.driver?.box,
      ...mountingTimeslotRegistration,
      box: mountingTimeslotRegistration?.box
        ? {
          ...mountingTimeslotRegistration.box,
          sealNumber:
                mountingTimeslotRegistration.box.sealNumber ?? '',
          firstCampaign:
                mountingTimeslotRegistration.box.firstCampaign?.['@id'],
          mountingDate: mountingTimeslotRegistration.box
            .mountingDate
            ? new Date(
              mountingTimeslotRegistration.box.mountingDate,
            )
            : new Date(),
        }
        : {
          firstCampaign: mountingTimeslotRegistration?.campaignRegistration?.campaign
            ? mountingTimeslotRegistration?.campaignRegistration?.campaign?.['@id']
            : undefined,
          mountingDate: mountingTimeslotRegistration?.timeslot?.startTime
            ? new Date(mountingTimeslotRegistration.timeslot.startTime)
            : undefined,
          type: 'v2',
          variant: undefined,
          sealNumber: undefined,
        },
      campaignRegistration: mountingTimeslotRegistration?.campaignRegistration
        ? mountingTimeslotRegistration.campaignRegistration?.['@id']
        : undefined,
    }
    : {
      comment: undefined,
      adminComment: undefined,
      type: undefined,
      variant: undefined,
      campaignRegistration: undefined,
      box: initialBox,
    });

const rawData = syncRef(internalMountingTimeslotRegistration, (mountingTimeslotRegistration) => ({
  ...initialData(mountingTimeslotRegistration),
}));

const boxTypes = enumOptionsProvider('/enums/BoxType');
const boxVariants = enumOptionsProvider('/enums/BoxVariant', (item) => ({
  value: item,
  label: t(`box.variant.${item}`),
}));

const {
  data, errors, validatedFn, setValidation,
} = useValidation(
  object({
    adminComment: string().nullable(),
    comment: string().nullable(),
    type: string().nullable().required(t('validation.notEmpty'))
  }),
  rawData,
);

function resetData() {
  rawData.value = { ...initialData() };
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    await exec(`${props.mountingTimeslotRegistration?.['@id']}`, {
      method: 'PUT',
      body: {
        box: props.mountingTimeslotRegistration?.type !== 'unmounting' && props.mountingTimeslotRegistration?.status !== 'pending' ? {
          id: props.mountingTimeslotRegistration?.box?.['@id'],
          sealNumber: data.value.box.sealNumber,
          mountingDate: data.value.box.mountingDate,
          type: data.value.box.type,
          variant: data.value.box.variant,
          status: data.value.box.status,
          firstCampaign: data.value.box.firstCampaign,
          alreadyPayed: data.value.box.alreadyPayed,
        } : undefined,
        comment: data.value.comment,
        adminComment: data.value.adminComment,
        campaignRegistration: data.value.campaignRegistration,
        type: data.value.type,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.mountings.mounting.mountingRegistrations.modal.edit.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.mountings.mounting.mountingRegistrations.modal.edit.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
